import { validateRetryPreview } from "../../utils/registry-retry-preview";
import api from "../../utils/api";
import AcConstant from "../../mixins/registry-batch/const/RegistryBatchAcConstant";

const state = {
  _batchList: [],
  _batchListTotal: 0,
  _batchListPage: 1,
  _batchListPageSize: 20,
  _currentBatch: null,
  _currentItems: [],
  _currentItemPage: { page: 1, pageSize: 20, totalCount: 0 },
  _currentStatusCounts: {},
  _currentSummary: null,
  _loading: false,
};

const mutations = {
  setBatchList(state, { items, total, page, pageSize }) {
    state._batchList = [];
    state._batchList = items || [];
    state._batchListTotal = total || 0;
    state._batchListPage = page || 1;
    state._batchListPageSize = pageSize || 20;
  },
  setBatchDetail(state, { batch, items, itemPage, statusCounts }) {
    state._currentBatch = batch || null;
    state._currentItems = [];
    state._currentItems = items || [];
    state._currentItemPage = itemPage || { page: 1, pageSize: 20, totalCount: 0 };
    state._currentStatusCounts = statusCounts || {};
  },
  setCurrentSummary(state, summary) {
    state._currentSummary = summary || null;
  },
  setLoading(state, loading) {
    state._loading = !!loading;
  },
  updateItemInList(state, updatedItem) {
    if (!updatedItem) return;
    const idx = state._currentItems.findIndex(i => i.itemSeq === updatedItem.itemSeq);
    if (idx >= 0) {
      // Vue 2 reactivity: 새 배열로 교체
      const next = state._currentItems.slice();
      next.splice(idx, 1, updatedItem);
      state._currentItems = next;
    }
  },
  setReset(state) {
    state._currentBatch = null;
    state._currentItems = [];
    state._currentItemPage = { page: 1, pageSize: 20, totalCount: 0 };
    state._currentStatusCounts = {};
    state._currentSummary = null;
  },
};

const actions = {
  // NZSC-2697 — 폴리곤 기반 일괄발급.
  // 엑셀 업로드 대신 WKT 폴리곤 1개를 백엔드(api-admin)로 보낸다.
  // 백엔드는 beaver 사이드카를 호출해 필지/호수를 자동 생성하고,
  // 이후 검색/승인/발급은 엑셀 업로드와 동일한 파이프라인을 재사용한다.
  async acUploadPolygonBatch({ commit }, { wkt, polygonName, uploader, companyName, buildableOnly, limit } = {}) {
    try {
      commit("setLoading", true);
      const body = {
        wkt,
        polygonName: polygonName || undefined,
        uploader: uploader || undefined,
        companyName: companyName || undefined,
      };
      if (typeof buildableOnly === "boolean") body.buildableOnly = buildableOnly;
      if (typeof limit === "number") body.limit = limit;

      const res = await api.post(AcConstant.POLYGON_UPLOAD, body);
      if (res.data?.code === 0) {
        // { batchId, totalCount, parcelCount, unitCount, unmatchedParcelCount }
        return res.data.data;
      }
      return null;
    } catch (err) {
      console.log("acUploadPolygonBatch err", err);
      return null;
    } finally {
      commit("setLoading", false);
    }
  },

  async acUploadBatch({ commit }, { file, options, parserProfile, issueType, uploader, companyName }) {
    try {
      commit("setLoading", true);
      const formData = new FormData();
      formData.append("file", file);
      if (options) {
        formData.append("options", typeof options === "string" ? options : JSON.stringify(options));
      }
      if (parserProfile) formData.append("parserProfile", parserProfile);
      if (issueType) formData.append("issueType", issueType);
      if (uploader) formData.append("uploader", uploader);
      if (companyName) formData.append("companyName", companyName);

      const res = await api.post(AcConstant.UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.code === 0) {
        return res.data.data; // { batchId, totalCount }
      }
      return null;
    } catch (err) {
      console.log("acUploadBatch err", err);
      return null;
    } finally {
      commit("setLoading", false);
    }
  },

  async acFetchBatchList({ commit }, { page = 1, pageSize = 20 } = {}) {
    try {
      commit("setLoading", true);
      const res = await api.get(AcConstant.LIST, {
        params: { page, pageSize },
      });
      if (res.data?.code === 0) {
        const d = res.data.data;
        commit("setBatchList", {
          items: d.items || [],
          total: d.total || 0,
          page: d.page || page,
          pageSize: d.pageSize || pageSize,
        });
        return d;
      }
      return null;
    } catch (err) {
      console.log("acFetchBatchList err", err);
      return null;
    } finally {
      commit("setLoading", false);
    }
  },

  async acFetchBatchDetail({ commit }, { batchId, itemPage = 1, itemPageSize = 50 } = {}) {
    try {
      const res = await api.get(AcConstant.DETAIL(batchId), {
        params: { itemPage, itemPageSize },
      });
      if (res.data?.code === 0) {
        const d = res.data.data;
        commit("setBatchDetail", {
          batch: d.batch,
          items: d.items || [],
          itemPage: d.itemPage,
          statusCounts: d.statusCounts || {},
        });
        return d;
      }
      return null;
    } catch (err) {
      console.log("acFetchBatchDetail err", err);
      return null;
    }
  },

  async acFetchBatchSummary({ commit }, batchId) {
    try {
      const res = await api.get(AcConstant.SUMMARY(batchId));
      if (res.data?.code === 0) {
        commit("setCurrentSummary", res.data.data);
        return res.data.data;
      }
      return null;
    } catch (err) {
      console.log("acFetchBatchSummary err", err);
      return null;
    }
  },

  async acPatchItem({ commit }, { batchId, itemSeq, patch }) {
    try {
      const res = await api.patch(AcConstant.PATCH_ITEM(batchId, itemSeq), patch);
      if (res.data?.code === 0) {
        commit("updateItemInList", res.data.data);
        return res.data.data;
      }
      return null;
    } catch (err) {
      console.log("acPatchItem err", err);
      return null;
    }
  },

  async acSelectCandidate({ commit }, { batchId, itemSeq, wkPin }) {
    console.log("[acSelectCandidate] POST", { batchId, itemSeq, wkPin });
    try {
      const res = await api.post(AcConstant.SELECT_CANDIDATE(batchId, itemSeq), { wkPin });
      console.log("[acSelectCandidate] response", res?.data);
      if (res.data?.code === 0) {
        commit("updateItemInList", res.data.data);
        return { ok: true, data: res.data.data };
      }
      return {
        ok: false,
        code: res.data?.code,
        message: res.data?.message || "알 수 없는 응답",
      };
    } catch (err) {
      console.log("[acSelectCandidate] error", err);
      const code = err?.data?.code ?? err?.status ?? "ERR";
      const message = err?.data?.message ?? "서버 응답 없음";
      return { ok: false, code, message, reason: err?.data?.reason };
    }
  },

  async acSetUniqueNo({ commit }, { batchId, itemSeq, uniqueNo, searchAddress }) {
    console.log("[acSetUniqueNo] POST", { batchId, itemSeq, uniqueNo, searchAddress });
    try {
      const body = { uniqueNo };
      if (searchAddress) body.searchAddress = searchAddress;
      const res = await api.post(AcConstant.SET_UNIQUE_NO(batchId, itemSeq), body);
      console.log("[acSetUniqueNo] response", res?.data);
      if (res.data?.code === 0) {
        // NZSC-2699: BE 가 { item, warning? } 래퍼를 내려줄 수도 있고, 구버전은 그냥 item.
        // 둘 다 호환되게 파싱.
        const payload = res.data.data;
        const itemObj = payload && typeof payload === "object" && "item" in payload ? payload.item : payload;
        const warning = payload && typeof payload === "object" && "warning" in payload ? payload.warning : null;
        commit("updateItemInList", itemObj);
        return { ok: true, data: itemObj, warning };
      }
      return {
        ok: false,
        code: res.data?.code,
        message: res.data?.message || "알 수 없는 응답",
      };
    } catch (err) {
      console.log("[acSetUniqueNo] error", err);
      const code = err?.data?.code ?? err?.status ?? "ERR";
      const message = err?.data?.message ?? "서버 응답 없음";
      return { ok: false, code, message, reason: err?.data?.reason };
    }
  },

  async acReSearchItem({ commit }, { batchId, itemSeq }) {
    try {
      const res = await api.post(AcConstant.RE_SEARCH(batchId, itemSeq));
      if (res.data?.code === 0) {
        commit("updateItemInList", res.data.data);
        return res.data.data;
      }
      return null;
    } catch (err) {
      console.log("acReSearchItem err", err);
      return null;
    }
  },

  // NZSC-2836: issueType override 지원. '1'=열람, '2'=발급. 미지정 시 batch 값 그대로 사용.
  async acIssueConfirm(_ctx, { batchId, itemSeqs, issueType }) {
    try {
      const body = {};
      if (itemSeqs && itemSeqs.length) body.itemSeqs = itemSeqs;
      if (issueType) body.issueType = issueType;
      const res = await api.post(AcConstant.ISSUE_CONFIRM(batchId), body);
      if (res.data?.code === 0) {
        return { ok: true, data: res.data.data };
      }
      return { ok: false, code: res.data?.code, message: res.data?.message || "알 수 없는 응답" };
    } catch (err) {
      // api.js interceptor 가 Promise.reject(error.response) 로 설정돼 있어
      // err 자체가 error.response. err.data = { code, message } 형태.
      console.log("acIssueConfirm err", err);
      const code = err?.data?.code ?? err?.status ?? "ERR";
      const message = err?.data?.message ?? "서버 응답 없음";
      return { ok: false, code, message, reason: err?.data?.reason };
    }
  },

  async acFetchRetryPreview(_ctx, input) {
    const { batchId, action, inputSeq, page = 0 } = input;
    const res = await api.get(`/api/v1/registry-batch/admin/${encodeURIComponent(batchId)}/retry-preview`, {
      params: { action, page, ...(action === "reissue" ? { inputSeq } : {}) },
    });
    return validateRetryPreview(res.data, input);
  },

  // NZSC-2827 — 배치 Resume: PDF 없는 건(FAILED + SEARCHED, uniqueNo 있음) 한꺼번에 재발급.
  // BE 는 setImmediate 로 백그라운드 실행 → FE 는 응답 받으면 polling 시작.
  async acResumeIssue(_ctx, { batchId, retryApproval }) {
    try {
      const res = await api.post(AcConstant.RESUME_ISSUE(batchId), retryApproval);
      if (res.data?.code === 0) {
        return { ok: true, data: res.data.data };
      }
      return { ok: false, code: res.data?.code, message: res.data?.message || "알 수 없는 응답" };
    } catch (err) {
      console.log("acResumeIssue err", err);
      const code = err?.data?.code ?? err?.status ?? "ERR";
      const message = err?.data?.message ?? "서버 응답 없음";
      return { ok: false, code, message, reason: err?.data?.reason };
    }
  },

  // NZSC-2827 — 단건 재발급: FAILED + uniqueNo 있는 행에 대해 PDF 발급만 다시 시도.
  async acReIssueItem({ commit }, { batchId, itemSeq, retryApproval }) {
    try {
      const res = await api.post(AcConstant.RE_ISSUE(batchId, itemSeq), retryApproval);
      if (res.data?.code === 0) {
        commit("updateItemInList", res.data.data);
        return { ok: true, data: res.data.data };
      }
      return { ok: false, code: res.data?.code, message: res.data?.message || "알 수 없는 응답" };
    } catch (err) {
      console.log("acReIssueItem err", err);
      const code = err?.data?.code ?? err?.status ?? "ERR";
      const message = err?.data?.message ?? "서버 응답 없음";
      return { ok: false, code, message, reason: err?.data?.reason };
    }
  },

  async acFetchDownloadUrl(_ctx, { batchId, kind = "zip" }) {
    try {
      const res = await api.get(AcConstant.DOWNLOAD_URL(batchId), {
        params: { kind },
      });
      if (res.data?.code === 0) {
        return res.data.data?.url || null;
      }
      return null;
    } catch (err) {
      console.log("acFetchDownloadUrl err", err);
      return null;
    }
  },

  acResetCurrent({ commit }) {
    commit("setReset");
  },
};

const getters = {};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
